import { useCallback, useMemo } from "react";
import type { FilterGroup } from "@/lib/filters";
import { EMPTY_GROUP } from "@/lib/filters";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Storage version for schema compatibility
const STORAGE_VERSION = "v1";

type ChannelFilterObject =
  | "account"
  | "opportunity"
  | "touchpoint"
  | "campaign";

interface ChannelFiltersState {
  account: FilterGroup;
  opportunity: FilterGroup;
  touchpoint: FilterGroup;
  campaign: FilterGroup;
}

const defaultState: ChannelFiltersState = {
  account: EMPTY_GROUP,
  opportunity: EMPTY_GROUP,
  touchpoint: EMPTY_GROUP,
  campaign: EMPTY_GROUP,
};

/**
 * Hook to manage channel filter state for Account, Opportunity & Touchpoint objects.
 *
 * - Persists state to sessionStorage for tab-specific isolation.
 * - Provides setters and clear helpers for each individual object type.
 * - Exposes convenience helpers for serialising the filters into the single
 *   `filters` query-parameter understood by the backend.
 */
export function useChannelFilterState(scope: string) {
  // Use sessionStorage for tab-specific filter isolation
  // Migrated from filters::v1 to metrics::filters::v1 for clarity
  const storageKey = `${scope}::metrics::filters::${STORAGE_VERSION}`;

  // ---------------------------------------------------------------------------
  // Persistence layer
  // ---------------------------------------------------------------------------

  const [state, setState] = useTrackedSessionStorage<ChannelFiltersState>(
    storageKey,
    defaultState,
    {
      // Migrate from old location
      migration: {
        from: {
          key: `${scope}::filters::${STORAGE_VERSION}`,
          storage: "sessionStorage",
          organizationScoped: true,
        },
        cleanup: true, // Remove the old key after migration
      },
    },
  );

  // ---------------------------------------------------------------------------
  // Update helpers
  // ---------------------------------------------------------------------------

  const setAccountFilters = useCallback(
    (group: FilterGroup) => {
      setState((prev) => ({
        account: group,
        opportunity: prev?.opportunity ?? EMPTY_GROUP,
        touchpoint: prev?.touchpoint ?? EMPTY_GROUP,
        campaign: prev?.campaign ?? EMPTY_GROUP,
      }));
    },
    [setState],
  );

  const setOpportunityFilters = useCallback(
    (group: FilterGroup) => {
      setState((prev) => ({
        account: prev?.account ?? EMPTY_GROUP,
        opportunity: group,
        touchpoint: prev?.touchpoint ?? EMPTY_GROUP,
        campaign: prev?.campaign ?? EMPTY_GROUP,
      }));
    },
    [setState],
  );

  const setTouchpointFilters = useCallback(
    (group: FilterGroup) => {
      setState((prev) => ({
        account: prev?.account ?? EMPTY_GROUP,
        opportunity: prev?.opportunity ?? EMPTY_GROUP,
        touchpoint: group,
        campaign: prev?.campaign ?? EMPTY_GROUP,
      }));
    },
    [setState],
  );

  const setCampaignFilters = useCallback(
    (group: FilterGroup) => {
      setState((prev) => ({
        account: prev?.account ?? EMPTY_GROUP,
        opportunity: prev?.opportunity ?? EMPTY_GROUP,
        touchpoint: prev?.touchpoint ?? EMPTY_GROUP,
        campaign: group,
      }));
    },
    [setState],
  );

  const clearAccountFilters = useCallback(() => {
    setState((prev) => ({
      account: EMPTY_GROUP,
      opportunity: prev?.opportunity ?? EMPTY_GROUP,
      touchpoint: prev?.touchpoint ?? EMPTY_GROUP,
      campaign: prev?.campaign ?? EMPTY_GROUP,
    }));
  }, [setState]);

  const clearOpportunityFilters = useCallback(() => {
    setState((prev) => ({
      account: prev?.account ?? EMPTY_GROUP,
      opportunity: EMPTY_GROUP,
      touchpoint: prev?.touchpoint ?? EMPTY_GROUP,
      campaign: prev?.campaign ?? EMPTY_GROUP,
    }));
  }, [setState]);

  const clearTouchpointFilters = useCallback(() => {
    setState((prev) => ({
      account: prev?.account ?? EMPTY_GROUP,
      opportunity: prev?.opportunity ?? EMPTY_GROUP,
      touchpoint: EMPTY_GROUP,
      campaign: prev?.campaign ?? EMPTY_GROUP,
    }));
  }, [setState]);

  const clearCampaignFilters = useCallback(() => {
    setState((prev) => ({
      account: prev?.account ?? EMPTY_GROUP,
      opportunity: prev?.opportunity ?? EMPTY_GROUP,
      touchpoint: prev?.touchpoint ?? EMPTY_GROUP,
      campaign: EMPTY_GROUP,
    }));
  }, [setState]);

  const clearAllFilters = useCallback(() => {
    setState({
      account: EMPTY_GROUP,
      opportunity: EMPTY_GROUP,
      touchpoint: EMPTY_GROUP,
      campaign: EMPTY_GROUP,
    });
  }, [setState]);

  // ---------------------------------------------------------------------------
  // Serialisation helpers – returns undefined when the group is effectively empty
  // ---------------------------------------------------------------------------

  const serialize = useCallback(
    (obj: ChannelFilterObject): string | undefined => {
      let group: FilterGroup;
      switch (obj) {
        case "account":
          group = state.account;
          break;
        case "opportunity":
          group = state.opportunity;
          break;
        case "touchpoint":
          group = state.touchpoint;
          break;
        case "campaign":
          group = state.campaign;
          break;
        default:
          throw new Error(`Unknown filter object: ${obj}`);
      }
      // Empty group results in `undefined` so we omit the query param entirely
      if (!group || group.filters.length === 0) return undefined;
      return JSON.stringify(group);
    },
    [state],
  );

  // Quick flags for UI badges etc.
  const hasAccountFilters = useMemo(
    () => state?.account?.filters?.length > 0,
    [state?.account],
  );
  const hasOpportunityFilters = useMemo(
    () => state?.opportunity?.filters?.length > 0,
    [state?.opportunity],
  );
  const hasTouchpointFilters = useMemo(
    () => state?.touchpoint?.filters?.length > 0,
    [state?.touchpoint],
  );
  const hasCampaignFilters = useMemo(
    () => state?.campaign?.filters?.length > 0,
    [state?.campaign],
  );

  return {
    // Raw groups
    accountFilters: state?.account ?? EMPTY_GROUP,
    opportunityFilters: state?.opportunity ?? EMPTY_GROUP,
    touchpointFilters: state?.touchpoint ?? EMPTY_GROUP,
    campaignFilters: state?.campaign ?? EMPTY_GROUP,

    // Helpers
    setAccountFilters,
    setOpportunityFilters,
    setTouchpointFilters,
    setCampaignFilters,
    clearAccountFilters,
    clearOpportunityFilters,
    clearTouchpointFilters,
    clearCampaignFilters,
    clearAllFilters,
    serialize,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    hasCampaignFilters,
  };
}
